import { useState, useEffect, useMemo } from 'react';

/**
 * Shared hook for managing settings sidebar visibility in preview dialogs
 * - Zero flicker: Init state from matchMedia
 * - Optional persistence: localStorage per-dialog
 */
export function useSettingsVisibility(options?: {
  storageKey?: string; // e.g., 'report-settings-visible'
  defaultVisible?: boolean; // Override default behavior
}) {
  // Detect mobile without flicker (run before first render)
  const isMobile = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
    []
  );

  // Init state: check localStorage > defaultVisible > auto (desktop: true, mobile: false)
  const [showSettings, setShowSettings] = useState(() => {
    if (options?.storageKey && typeof window !== 'undefined') {
      const saved = localStorage.getItem(options.storageKey);
      if (saved !== null) return JSON.parse(saved);
    }
    if (options?.defaultVisible !== undefined) return options.defaultVisible;
    return !isMobile; // Default: desktop true, mobile false
  });

  // Persist to localStorage if key provided
  useEffect(() => {
    if (options?.storageKey) {
      localStorage.setItem(options.storageKey, JSON.stringify(showSettings));
    }
  }, [showSettings, options?.storageKey]);

  // Optional: Keyboard shortcut ([) to toggle
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '[' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // Only if not in input/textarea
        const target = e.target as HTMLElement;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          setShowSettings(v => !v);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return { isMobile, showSettings, setShowSettings };
}
